import { z } from 'zod'

export const numberOrStringSchema = z.union([z.number(), z.string().trim()])
// Timestamp lu à deux endroits : côté lambda sur la row Kysely (Date) et côté web sur le JSON HTTP
// (string ISO). Accepte les deux formes et normalise vers la string ISO.
export const dateToIsoStringSchema = z.union([z.string(), z.date()]).transform((value) => (value instanceof Date ? value.toISOString() : value))

export const numberOrStringToNumberSchema = numberOrStringSchema.transform((v, ctx) => {
  const parsed = Number.parseInt(v.toString(), 10)
  if (Number.isNaN(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Not a number',
    })

    return z.NEVER
  }

  return parsed
})

export const numberOrStringToNumberFloatSchema = numberOrStringSchema.transform((v, ctx) => {
  const parsed = Number.parseFloat(v.toString())
  if (Number.isNaN(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Not a number',
    })

    return z.NEVER
  }

  return parsed
})

const orAndSchema = z.union([
  z.tuple([
    z.object({
      symbol: z.string(),
      value: z.number(),
    }),
    z.object({
      symbol: z.string(),
      value: z.number(),
    }),
  ]),
  z.tuple([
    z.object({
      symbol: z.string(),
      value: z.number(),
    }),
  ]),
])

export const conditionSchema = z.object({
  and: orAndSchema.optional(),
  or: orAndSchema.optional(),
  durationMinutes: z.number().optional(),
  forceConformity: z.boolean().optional(),
})

// Caractères de largeur nulle et marques invisibles à supprimer : ZWSP (U+200B),
// marques directionnelles (U+200E/U+200F), trait d'union conditionnel (U+00AD),
// gluon de mots (U+2060), BOM (U+FEFF). On EXCLUT volontairement ZWNJ (U+200C) et
// ZWJ (U+200D), porteurs de sens (séquences emoji, orthographe persane/arabe).
const INVISIBLE_CHARS_REGEX = /[\u200B\u200E\u200F\u00AD\u2060\uFEFF]/g
// Espaces horizontaux Unicode à réduire à une espace simple : tabulation et
// catégorie Zs (insécable, fine, etc.). On NE touche PAS aux retours à la ligne
// pour préserver la mise en forme des champs multi-lignes (commentaires, incidents).
const HORIZONTAL_WHITESPACE_REGEX = /[\t\p{Zs}]+/gu

// Assainit une chaîne libre saisie par l'utilisateur (typiquement copiée-collée
// depuis Word/PDF/web), pour éviter que des caractères invisibles cassent la
// recherche, le tri ou l'égalité. Dans l'ordre :
//   1. normalise en forme canonique NFC (fusionne « e + accent combinant » -> « é »)
//   2. supprime les caractères de largeur nulle et marques invisibles
//   3. réduit toute suite d'espaces horizontaux Unicode (insécables, fines, etc.) à une espace simple
//   4. supprime les espaces en début/fin
// Exposée en fonction pure pour pouvoir aligner une comparaison back (ex. dédup de
// noms issus de sources non assainies) sur ce que cleanStringSchema stocke.
export const cleanString = (value: string) => value.normalize('NFC').replaceAll(INVISIBLE_CHARS_REGEX, '').replaceAll(HORIZONTAL_WHITESPACE_REGEX, ' ').trim()

export const cleanStringSchema = z.string().transform(cleanString)

// Variante de cleanStringSchema pour les champs obligatoires : rejette une saisie
// qui devient vide après nettoyage (ex. «   » ou « " " » -> ''), pour éviter de
// créer des entrées au nom vide affiché blanc dans les listes.
export const requiredCleanStringSchema = cleanStringSchema.pipe(z.string().min(1))

// Paramètre de query string en CSV : rejette les éléments vides (« a,,b », virgule en tête ou en fin), puis valide
// chaque élément avec itemSchema.
export const csvArraySchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z
    .string()
    .regex(/^(?!.*,,)(?!.*,$)(?!^,).*$/)
    .transform((value) => value.split(','))
    .pipe(z.array(itemSchema))

export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

const hourScheduleSchema = z.object({
  setPoint: z.number(),
  hours: z.array(z.number()).length(2),
})
export const scheduleSchema = z.object({
  0: z.array(hourScheduleSchema),
  1: z.array(hourScheduleSchema),
  2: z.array(hourScheduleSchema),
  3: z.array(hourScheduleSchema),
  4: z.array(hourScheduleSchema),
  5: z.array(hourScheduleSchema),
  6: z.array(hourScheduleSchema),
})

const LatLngSchema = z.any()

export const polygonSchema = z.union([z.array(LatLngSchema), z.array(z.array(LatLngSchema)), z.array(z.array(z.array(LatLngSchema)))])

export const floorPlanSpaceSchema = z.object({
  spaceId: numberOrStringToNumberSchema,
  polygon: polygonSchema,
  tooltipWidth: z.number().optional(),
  tooltipHeight: z.number().optional(),
  tooltipDisplay: z.enum(['row', 'column']).optional(),
})

// Legionella sensor plans pin a device instead of a space location: the key is a
// uuid, and a sensor has no space of its own. Kept as a sibling schema rather than
// relaxing spaceId to optional, which would break the four space-keyed plan kinds.
export const floorPlanSensorSpaceSchema = z.object({
  deviceId: z.string().uuid(),
  polygon: polygonSchema,
  tooltipWidth: z.number().optional(),
  tooltipHeight: z.number().optional(),
  tooltipDisplay: z.enum(['row', 'column']).optional(),
})

// Sensor variant first: a device pin carries no spaceId, so the space schema would
// reject it, while a space pin never matches the sensor schema.
export const floorPlanAnySpaceSchema = z.union([floorPlanSensorSpaceSchema, floorPlanSpaceSchema])

export type FloorPlanSpace = z.infer<typeof floorPlanSpaceSchema>
export type FloorPlanSensorSpace = z.infer<typeof floorPlanSensorSpaceSchema>
export type FloorPlanAnySpace = z.infer<typeof floorPlanAnySpaceSchema>

export const isFloorPlanSensorSpace = (space: FloorPlanAnySpace): space is FloorPlanSensorSpace => 'deviceId' in space

// Space-keyed plan kinds (comfort, lighting, airQuality, electrical) read spaceId
// directly. A plan row only ever holds one variant — floor_plan is keyed by type —
// so this narrows without dropping anything in practice.
export const isFloorPlanSpace = (space: FloorPlanAnySpace): space is FloorPlanSpace => !isFloorPlanSensorSpace(space)
